/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  FieldUniqueIdentifier,
  IRegistryGetter,
  ServiceName,
  StructureBuilder,
  resolvePath,
} from "./types"

/**
 * Retrieves a value from a nested object using a dot-notation path string.
 * Returns undefined if the path doesn't exist.
 * Does not support array indexing in the path (e.g., 'a.b[0].c').
 *
 * @param obj - The object to traverse.
 * @param path - The dot-notation path string (e.g., 'a.b.c').
 * @returns The value at the specified path, or undefined if not found.
 */
export function getValueByPath(obj: any, path: string): unknown {
  // Basic safety checks
  if (typeof obj !== "object" || obj === null || !path) {
    return undefined
  }

  const properties: string[] = path.split(".")
  let current: any = obj

  for (const prop of properties) {
    if (typeof current !== "object" || current === null || !(prop in current)) {
      return undefined // Path segment not found or cannot traverse further
    }
    current = current[prop]
  }

  return current
}

/**
 * Sets a value in a nested object using a dot-notation path string.
 * Creates intermediate objects if they don't exist.
 * Overwrites existing values at the final path segment.
 * Does not support array indexing or creation in the path.
 * Mutates the input object.
 *
 * @param obj - The object to modify. Must be a non-null object.
 * @param path - The dot-notation path string (e.g., 'a.b.c').
 * @param value - The value to set at the specified path.
 * @throws {Error} If the input obj is not a valid object or path is empty.
 * @throws {Error} If an intermediate path segment exists but is not an object.
 */
export function setValueByPath(
  obj: Record<string, any>,
  path: string,
  value: unknown,
): void {
  if (typeof obj !== "object" || obj === null) {
    throw new Error('setValueByPath: Input "obj" must be a non-null object.')
  }
  if (typeof path !== "string" || path.length === 0) {
    throw new Error('setValueByPath: Input "path" must be a non-empty string.')
  }

  const properties: string[] = path.split(".")
  let current: any = obj

  for (let i = 0; i < properties.length - 1; i++) {
    const prop = properties[i]

    // If the path segment doesn't exist, create an object.
    if (current[prop] === undefined || current[prop] === null) {
      current[prop] = {}
    }
    // If it exists but isn't an object, we cannot proceed.
    else if (typeof current[prop] !== "object") {
      throw new Error(
        `setValueByPath: Path segment "${prop}" exists but is not an object.`,
      )
    }

    current = current[prop]
  }

  // Set the value at the final segment.
  const finalProp = properties[properties.length - 1]
  current[finalProp] = value
}

/**
 * Checks if a path string contains the array wildcard [*].
 * @param path - The path string.
 * @returns True if the path contains [*], false otherwise.
 */
export function pathContainsWildcard(path: string): boolean {
  return path.includes("[*]")
}

/**
 * Extracts the base path before the first wildcard.
 * Example: "items[*].value" -> "items"
 * Example: "users[0].addresses[*].street" -> "users[0].addresses"
 * @param path - The path string containing a wildcard.
 * @returns The base path string, or the original path if no wildcard is found.
 */
export function getBasePathFromWildcard(path: string): string {
  const wildcardIndex = path.indexOf("[*]")
  if (wildcardIndex === -1) {
    return path // No wildcard found
  }
  // Find the last dot or opening bracket *before* the wildcard
  const lastSeparator = Math.max(
    path.lastIndexOf(".", wildcardIndex),
    path.lastIndexOf("[", wildcardIndex),
  )
  // If no separator before, return the part before wildcard, otherwise return up to separator
  return lastSeparator === -1
    ? path.substring(0, wildcardIndex)
    : path.substring(0, lastSeparator)

  // Simpler alternative if we only care about the top-level array:
  // return path.substring(0, path.indexOf('[*]'));
}

/**
 * Extracts the relative path after the first wildcard's closing bracket.
 * Example: "items[*].value" -> "value"
 * Example: "users[*].addresses[0].street" -> "addresses[0].street"
 * @param path - The path string containing a wildcard.
 * @returns The relative path string, or empty string if no wildcard/suffix exists.
 */
export function getRelativePathAfterWildcard(path: string): string {
  const wildcardEndIndex = path.indexOf("[*]")
  if (wildcardEndIndex === -1) {
    return "" // No wildcard
  }
  // Find the position after '[*]'
  const afterWildcardIndex = wildcardEndIndex + 3 // Length of '[*]'
  // Skip potential dot separator immediately after wildcard
  if (path.charAt(afterWildcardIndex) === ".") {
    return path.substring(afterWildcardIndex + 1)
  }
  return path.substring(afterWildcardIndex)
}

/**
 * Replaces the first wildcard [*] with a specific index [i].
 * Example: ("items[*].value", 0) -> "items[0].value"
 * @param path - The path string with a wildcard.
 * @param index - The index to substitute.
 * @returns The resolved path string.
 */
export function resolveWildcardPath(path: string, index: number): string {
  return path.replace("[*]", `[${index}]`)
}

/**
 * Recursively retrieves all values from a nested object using a path string that may contain multiple [*] wildcards.
 * Returns an array if any wildcard is present, otherwise a single value.
 * Example: getValueByPathWithWildcards(obj, "a[*].b[*].c") returns all c's in all b's in all a's.
 */
export function getValueByPathWithWildcards(obj: any, path: string): unknown {
  if (typeof obj !== "object" || obj === null || !path) return undefined
  const segments = path.split(".")
  function helper(current: any, segIdx: number): any {
    if (segIdx >= segments.length) return current
    const segment = segments[segIdx]
    const wildcardIdx = segment.indexOf("[*]")
    if (wildcardIdx !== -1) {
      const arrKey = segment.slice(0, wildcardIdx)
      const arr = arrKey ? current?.[arrKey] : current
      if (!Array.isArray(arr)) return []
      return arr.map((item) => helper(item, segIdx + 1))
    } else {
      return helper(current?.[segment], segIdx + 1)
    }
  }
  // Do NOT flatten, return the nested structure
  return helper(obj, 0)
}

/**
 * Recursively sets values in a nested object using a path string that may contain multiple [*] wildcards.
 * If the path contains wildcards, values must be an array of the same shape.
 * Mutates the input object.
 */
export function setValueByPathWithWildcards(
  obj: any,
  path: string,
  value: any,
): void {
  const segments = path.split(".")
  function helper(current: any, segIdx: number, val: any): void {
    if (segIdx === segments.length) return
    const segment = segments[segIdx]
    const wildcardIdx = segment.indexOf("[*]")
    if (wildcardIdx !== -1) {
      const arrKey = segment.slice(0, wildcardIdx)
      if (!current[arrKey]) current[arrKey] = []
      if (!Array.isArray(current[arrKey]))
        throw new Error(
          `setValueByPathWithWildcards: Expected array at ${arrKey}`,
        )
      if (!Array.isArray(val))
        throw new Error(
          "setValueByPathWithWildcards: Value must be array for wildcard path",
        )
      for (let i = 0; i < val.length; i++) {
        if (segIdx === segments.length - 1) {
          current[arrKey][i] = val[i]
        } else {
          if (!current[arrKey][i]) current[arrKey][i] = {}
          helper(current[arrKey][i], segIdx + 1, val[i])
        }
      }
    } else {
      if (segIdx === segments.length - 1) {
        current[segment] = val
      } else {
        if (!current[segment]) current[segment] = {}
        helper(current[segment], segIdx + 1, val)
      }
    }
  }
  helper(obj, 0, value)
}

/**
 * A recursive helper to build out a path that may contain multiple levels of arrays.
 * It processes one segment of the path at a time.
 * @param currentObject - The object level to which we are currently adding properties.
 * @param path - The remaining path string to process.
 */
function buildPathRecursively(
  currentObject: Record<string, any>,
  path: string,
): void {
  // Base case: If the path is simple (no wildcards), use the original utility and stop.
  if (!pathContainsWildcard(path)) {
    setValueByPath(currentObject, path, true)
    return
  }

  // --- Recursive Step: Handle the first level of array nesting ---

  // 1. Deconstruct the path.
  const basePath = getBasePathFromWildcard(path) // e.g., "products"
  const relativePath = getRelativePathAfterWildcard(path) // e.g., "packages[*].type"

  // 2. Ensure the array container exists at the base path.
  let arrayContainer = getValueByPath(currentObject, basePath) as
    | any[]
    | undefined
  if (!arrayContainer) {
    arrayContainer = [{}] // Create array with a template object
    setValueByPath(currentObject, basePath, arrayContainer)
  } else if (!Array.isArray(arrayContainer)) {
    throw new Error(
      `Structure builder conflict: Path "${basePath}" was expected to be an array but is not.`,
    )
  } else if (arrayContainer.length === 0) {
    arrayContainer.push({}) // Ensure template object exists
  }

  // 3. The new context for the next recursive call is the template object.
  const templateItem = arrayContainer[0]

  // 4. If there's a relative path left to process, recurse.
  if (relativePath) {
    buildPathRecursively(templateItem, relativePath)
  }
}

/**
 * A higher-order, curried function that creates a template object structure builder.
 * This version uses a recursive helper to correctly handle multi-level nested arrays.
 */
export const serviceStructureBuilder =
  (registry: IRegistryGetter) =>
  (serviceName: ServiceName): StructureBuilder =>
  (fuids: ReadonlyArray<FieldUniqueIdentifier>): Record<string, any> => {
    const structure: Record<string, any> = {}

    for (const fuid of fuids) {
      const fieldDef = registry.get(fuid)
      const path = resolvePath(fieldDef?.serviceRepresentations[serviceName])

      if (!path) {
        continue
      }

      try {
        // Start the recursive build process for each FUID's path from the root structure.
        buildPathRecursively(structure, path)
      } catch (error) {
        console.error(
          `FieldAnchor: Failed to build structure for FUID "${fuid}" with path "${path}".`,
          error,
        )
      }
    }

    return structure
  }

// Helper function to remove leading underscore from FUID for PHYTO_PDF paths
export const toPhytoPdfPath = (fuid: string): string =>
  fuid.startsWith("_") ? fuid.slice(1) : fuid
